import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import _ from "lodash";
import { useLoaderData } from "@remix-run/react";
import { BaseLayout } from "~/layouts/main/MainLayout";
import { BookProductPage } from "~/pages/BookProduct";
import { bookPageData } from "~/data-store.server";

import swiperCss from "swiper/css?url";
import swiperNavigation from "swiper/css/navigation?url";
import swiperPagination from "swiper/css/pagination?url";
import bookSwiperCss from "~/pages/swiper.css?url";
import { metaOg } from "~/utils/meta";
import type { HandleGTagEvent } from "~/utils/gTagEvent";
import type { HandleStructuredData } from "~/utils/structuredData";
import type { HandleDynamicLinks } from "~/utils/links";
import { bookStructuredData } from "~/utils/book-structured-data";

type HandleType = HandleGTagEvent<Awaited<ReturnType<typeof loader>>> &
  HandleStructuredData<Awaited<ReturnType<typeof loader>>> &
  HandleDynamicLinks<Awaited<ReturnType<typeof loader>>>;

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: swiperCss },
  { rel: "stylesheet", href: swiperNavigation },
  { rel: "stylesheet", href: swiperPagination },
  { rel: "stylesheet", href: bookSwiperCss },
];

export const handle: HandleType = {
  dynamicLinks: (data) => [
    {
      rel: "canonical",
      href: `https://dystans.cc/ksiazka/${data.slugUrl}`,
    },
  ],
  structuredData(bookData) {
    return bookStructuredData(bookData);
  },
  gTagEvent(data) {
    const price = data.priceInfo.discountPrice
      ? data.priceInfo.discountPrice
      : data.priceInfo.regularPrice;
    return [
      {
        eventName: "view_item",
        payload: {
          currency: "PLN",
          value: parseFloat(price.replace(",", ".")).toFixed(2),
        },
      },
    ];
  },
};

export async function loader({ params }: LoaderFunctionArgs) {
  const books = await bookPageData();

  const book = books.find((item) => {
    return item.slugUrl === params.bookSlug;
  });

  if (!book) {
    throw new Response(null, {
      status: 404,
      statusText: "Not Found",
    });
  }

  book._join_variants = book.variants
    .map(({ productId }) => {
      const nestedVariant = books.find((item) => {
        return item.id === productId;
      });
      if (nestedVariant) {
        return _.cloneDeep(nestedVariant);
      }
    })
    .filter((item): item is typeof book => Boolean(item));

  return book;
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) {
    return [];
  }
  const imgSrc =
    data.images.find((img) => {
      return img.type === "product-cover";
    })?.src || data.images[0].src;

  const metaTitle = data.meta.find(
    (item): item is { title: string } => "title" in item
  );

  const metaDescription = data.meta.find(
    (item): item is { name: string; content: string } =>
      "name" in item && item.name === "description"
  );

  const metaOgData = metaOg({
    pageType: "book",
    siteName: "Wydawnictwo Dystans",
    title: metaTitle?.title || "",
    description: metaDescription?.content || "",
    image: new URL(
      imgSrc ? imgSrc : "images/dystans_sygnet_orange.png",
      "https://dystans.cc"
    ).toString(),
    url: new URL(`/ksiazka/${data.slugUrl}`, "https://dystans.cc").toString(),
  });
  return [...data.meta, ...metaOgData];
};

export default function Index() {
  const productData = useLoaderData<typeof loader>();
  return (
    <BaseLayout>
      <BookProductPage bookInfo={productData} />
    </BaseLayout>
  );
}
